'use client'
import { payssionSubscribe, subscribe, type PaypalSubscribeData } from "@/apis/pricing"
import { useToast } from "@/provider/ToastProvider"
import { Confirm } from "@/ui/Confirm"
import { DarkButton, LightButton } from "@/ui/Button"
import { useDisclosure } from "@heroui/react"
import { useMutation } from "@tanstack/react-query"
import { useTranslations } from "next-intl"
import { useCallback, useRef, type ReactNode } from "react"
import UpgradeMembershipModal from "./UpgradeMembershipModal"
import {
  getPendingSubscribeGoodsId,
  markPendingPremiumSubscribe,
  peekPendingPremiumSubscribe,
  type PendingSubscribeVia,
} from "./pendingSubscribe"
import { savePayRedirect } from "@/util/payRedirect"

export const useContinuePayment = (): {
  continuePayment: () => boolean
  subscribeByGoodsId: (id: number, nextVia?: PendingSubscribeVia) => boolean
  isLoading: boolean
  dialogs: ReactNode
} => {
  const t = useTranslations()
  const { showToast } = useToast()
  const confirmModal = useDisclosure()
  const upgradeModal = useDisclosure()
  const url = useRef("")
  const upgradeConfirm = useRef("")
  const goodsId = useRef(0)
  const via = useRef<"paypal" | "payssion">("paypal")

  const handleSubscribeRes = (res: { status: number; data?: PaypalSubscribeData; message?: string; msg?: string }) => {
    if (res.status !== 200) {
      showToast({ type: "error", message: res.message })
      return
    }

    switch (Number(res.data?.status)) {
      case 200:
        upgradeModal.onClose()
        if (res.data?.link) {
          markPendingPremiumSubscribe(goodsId.current, via.current)
          window.open(res.data.link, "_self")
        }
        break
      case 204:
        upgradeModal.onClose()
        url.current = res.data?.link || ""
        confirmModal.onOpen()
        break
      case 209:
        upgradeModal.onClose()
        showToast({ message: t("pricing.planAlreadyActive") })
        break
      case 210:
        upgradeModal.onClose()
        showToast({ message: t("pricing.planIncludedInCurrent") })
        break
      case 211:
        upgradeConfirm.current = res.data?.upgrade_confirm || ""
        upgradeModal.onOpen()
        break
      case 0:
        upgradeModal.onClose()
        showToast({ type: "error", message: t("pricing.paypalNotConfigured") })
        break
      default:
        showToast({ type: "error", message: res.message || res.msg })
    }
  }

  const paypalMuta = useMutation({
    mutationFn: subscribe,
    onSuccess: handleSubscribeRes,
  })

  const payssionMuta = useMutation({
    mutationFn: payssionSubscribe,
    onSuccess(res) {
      if (res.status === 200) {
        markPendingPremiumSubscribe(goodsId.current, "payssion")
        window.open(res.data.link, "_self")
      } else {
        showToast({ type: "error", message: res.message })
      }
    },
  })

  const subscribeByGoodsId = useCallback((id: number, nextVia: PendingSubscribeVia = "paypal") => {
    const nextGoodsId = Number(id)
    if (!Number.isFinite(nextGoodsId) || nextGoodsId <= 0) return false

    savePayRedirect()
    goodsId.current = nextGoodsId
    via.current = nextVia

    if (nextVia === "payssion") {
      payssionMuta.mutate({ goods_id: nextGoodsId })
    } else {
      paypalMuta.mutate({ goods_id: nextGoodsId })
    }
    return true
  }, [paypalMuta.mutate, payssionMuta.mutate])

  const continuePayment = useCallback(() => {
    const pending = peekPendingPremiumSubscribe()
    const nextGoodsId = getPendingSubscribeGoodsId()
    if (!nextGoodsId) return false
    return subscribeByGoodsId(nextGoodsId, pending?.via === "payssion" ? "payssion" : "paypal")
  }, [subscribeByGoodsId])

  const onClickOpen = () => {
    if (!url.current) return
    savePayRedirect()
    markPendingPremiumSubscribe(goodsId.current, via.current)
    window.open(url.current, "_self")
    url.current = ""
    confirmModal.onClose()
  }

  const onConfirmUpgrade = () => {
    paypalMuta.mutate({
      goods_id: goodsId.current,
      upgrade_confirm: upgradeConfirm.current,
    })
  }

  const isLoading = paypalMuta.isPending || payssionMuta.isPending

  const dialogs = (
    <>
      <Confirm
        message="You have a pending order. Please proceed to pay or cancel the payment."
        title=""
        isOpen={confirmModal.isOpen}
        onOpenChange={confirmModal.onOpenChange}
        backdrop="blur"
        footer={
          <>
            <LightButton className="w-[140px]" onPress={confirmModal.onClose}>{t("common.cancel")}</LightButton>
            <DarkButton className="w-[140px]" onPress={onClickOpen}>{t("pricing.goHandleIt")}</DarkButton>
          </>
        }
      />
      <UpgradeMembershipModal
        isOpen={upgradeModal.isOpen}
        onOpenChange={upgradeModal.onOpenChange}
        isLoading={paypalMuta.isPending}
        onConfirm={onConfirmUpgrade}
      />
    </>
  )

  return { continuePayment, subscribeByGoodsId, isLoading, dialogs }
}
